import React, { useState, useMemo, useEffect } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  Checkbox,
  FormControlLabel,
  Card,
  CardContent,
  Grid,
  IconButton,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
  Paper,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CardHeader,
} from "@mui/material";
import {
  Image as ImageIcon,
  Checklist,
  VideoLibrary,
  Add as AddIcon,
  Delete as DeleteIcon,
  TextFields as TextIcon,
  CheckBox as MultiSelectIcon,
  RadioButtonChecked as SingleSelectIcon,
} from "@mui/icons-material";

import {
  ChecklistTemplateRequest,
  UISection,
  UIItem,
  QuestionType,
  emptyTemplate,
  createSection,
  createItem,
  createOption,
  validateAll,
  hasAnyErrors,
  transformSectionTypeForDB,
  transformQuestionTypeForDB,
  ChecklistTemplateFullResponse,
} from "./types/checklistTemplateTypes";
import type { ChecklistTemplate } from "./types/checklistTemplateTypes";
import localStorageHelper from "app/localStorage";
import { User } from "components/authentication/models/authTypes";

interface ChecklistTemplateProps {
  onTemplateCreated?: () => void;
  onFormDataChange?: (
    templateData: ChecklistTemplateRequest,
    hasErrors: boolean
  ) => void;
  initialData?: ChecklistTemplateFullResponse | null;
  isEditMode?: boolean;
}

const ChecklistTemplateComponent: React.FC<ChecklistTemplateProps> = ({
  onTemplateCreated,
  onFormDataChange,
  initialData,
  isEditMode = false,
}) => {
  const theme = useTheme();
  const [user] = useState<User | undefined>(
    localStorageHelper.getByKey("user")
  );

  // Dialog state for showing full text
  const [textDialog, setTextDialog] = useState<{
    open: boolean;
    title: string;
    text: string;
  }>({
    open: false,
    title: "",
    text: "",
  });

  // Helper to create default sections for new template
  const createDefaultSections = (): UISection[] => {
    const images = createSection("Images", "Images");

    const defaultImageItems: UIItem[] = [
      {
        ...createItem(images.sectionId, "Images"),
        itemName: "Front",
        isSelected: false,
        enteredBy: user?.userName || "system",
        modifiedBy: user?.userName || "system",
      },
      {
        ...createItem(images.sectionId, "Images"),
        itemName: "Left",
        isSelected: false,
        enteredBy: user?.userName || "system",
        modifiedBy: user?.userName || "system",
      },
      {
        ...createItem(images.sectionId, "Images"),
        itemName: "Back",
        isSelected: false,
        enteredBy: user?.userName || "system",
        modifiedBy: user?.userName || "system",
      },
      {
        ...createItem(images.sectionId, "Images"),
        itemName: "Right",
        isSelected: false,
        enteredBy: user?.userName || "system",
        modifiedBy: user?.userName || "system",
      },
    ];

    return [
      {
        ...images,
        items: defaultImageItems,
        isFixed: true,
        enteredBy: user?.userName || "system",
        modifiedBy: user?.userName || "system",
      },
    ];
  };

  // Template state - initialized from initialData if in edit mode
  const [template, setTemplate] = useState<ChecklistTemplate>(() => {
    if (isEditMode && initialData?.template) {
      return {
        ...initialData.template,
        modifiedBy: user?.userName || "system",
      };
    }
    return {
      ...emptyTemplate(),
      enteredBy: user?.userName || "system",
      modifiedBy: user?.userName || "system",
    };
  });

  // Sections state - initialized from initialData if in edit mode
  const [sections, setSections] = useState<UISection[]>(() => {
    if (isEditMode && initialData?.sections && initialData.sections.length > 0) {
      return initialData.sections.map((section) => ({
        ...section,
        modifiedBy: user?.userName || "system",
        items: section.items.map((item) => ({
          ...item,
          modifiedBy: user?.userName || "system",
        })),
      }));
    }
    return createDefaultSections();
  });

  // Reset state when initialData changes (for switching between templates)
  useEffect(() => {
    if (isEditMode && initialData) {
      setTemplate({
        ...initialData.template,
        modifiedBy: user?.userName || "system",
      });
      setSections(
        initialData.sections.map((section) => ({
          ...section,
          modifiedBy: user?.userName || "system",
          items: section.items.map((item) => ({
            ...item,
            modifiedBy: user?.userName || "system",
          })),
        }))
      );
    } else if (!isEditMode) {
      setTemplate({
        ...emptyTemplate(),
        enteredBy: user?.userName || "system",
        modifiedBy: user?.userName || "system",
      });
      setSections(createDefaultSections());
    }
  }, [initialData, isEditMode, user?.userName]);

  // Ensure request validity
  const errors = useMemo(
    () => validateAll(template, sections),
    [template, sections]
  );

  /**************
   * Handlers   *
   **************/
  const addChecklistSection = () => {
    setSections((prev) => [
      ...prev,
      {
        ...createSection("Checklist", ""),
        enteredBy: user?.userName || "system",
        modifiedBy: user?.userName || "system",
      },
    ]);
  };

  const updateSection = (sectionId: number, patch: Partial<UISection>) => {
    setSections((prev) =>
      prev.map((s) => (s.sectionId === sectionId ? { ...s, ...patch } : s))
    );
  };

  const removeSection = (sectionId: number) => {
    setSections((prev) => prev.filter((s) => s.sectionId !== sectionId));
  };

  const addItem = (sectionId: number) => {
    setSections((prev) =>
      prev.map((s) => {
        if (s.sectionId !== sectionId) return s;
        const newItem = {
          ...createItem(sectionId, s.sectionType),
          enteredBy: user?.userName || "system",
          modifiedBy: user?.userName || "system",
        };
        return { ...s, items: [...s.items, newItem] };
      })
    );
  };

  const updateItem = (
    sectionId: number,
    itemId: number,
    patch: Partial<UIItem>
  ) => {
    setSections((prev) =>
      prev.map((s) => {
        if (s.sectionId !== sectionId) return s;
        return {
          ...s,
          items: s.items.map((it) =>
            it.itemId === itemId ? { ...it, ...patch } : it
          ),
        };
      })
    );
  };

  const removeItem = (sectionId: number, itemId: number) => {
    setSections((prev) =>
      prev.map((s) =>
        s.sectionId === sectionId
          ? { ...s, items: s.items.filter((it) => it.itemId !== itemId) }
          : s
      )
    );
  };

  const setQuestionType = (
    sectionId: number,
    itemId: number,
    q: QuestionType
  ) => {
    setSections((prev) =>
      prev.map((s) => {
        if (s.sectionId !== sectionId) return s;
        return {
          ...s,
          items: s.items.map((it) => {
            if (it.itemId !== itemId) return it;
            return { ...it, questionType: q };
          }),
        };
      })
    );
  };

  const addOption = (
    sectionId: number,
    itemId: number,
    optionText?: string
  ) => {
    setSections((prev) =>
      prev.map((s) => {
        if (s.sectionId !== sectionId) return s;
        return {
          ...s,
          items: s.items.map((it) => {
            if (it.itemId !== itemId) return it;
            const newOption = {
              ...createOption(itemId),
              optionText: optionText || "",
            };
            if (it.questionType === "Multi-Select") {
              return { ...it, multiSelectOptions: [...it.multiSelectOptions, newOption] };
            } else if (it.questionType === "Single-Select") {
              return { ...it, singleSelectOptions: [...it.singleSelectOptions, newOption] };
            }
            return it;
          }),
        };
      })
    );
  };

  const removeOption = (
    sectionId: number,
    itemId: number,
    optionId: number
  ) => {
    setSections((prev) =>
      prev.map((s) => {
        if (s.sectionId !== sectionId) return s;
        return {
          ...s,
          items: s.items.map((it) => {
            if (it.itemId !== itemId) return it;
            if (it.questionType === "Multi-Select") {
              return {
                ...it,
                multiSelectOptions: it.multiSelectOptions.filter((o) => o.optionId !== optionId),
              };
            } else if (it.questionType === "Single-Select") {
              return {
                ...it,
                singleSelectOptions: it.singleSelectOptions.filter((o) => o.optionId !== optionId),
              };
            }
            return it;
          }),
        };
      })
    );
  };

  /********************************
   * Compose API request payload  *
   ********************************/
  const request: ChecklistTemplateRequest = useMemo(() => {
    const flatSections = sections.map((s) => ({
      sectionId: s.sectionId, // < 0 ? undefined : s.sectionId, // let server assign new positive IDs
      sectionName: s.sectionName,
      checkListTemplateId:
        template.templateId && template.templateId > 0
          ? template.templateId
          : undefined,
      isActive: s.isActive,
      sectionType: transformSectionTypeForDB(s.sectionType), // Transform for DB storage
      enteredBy: s.enteredBy,
      modifiedBy: s.modifiedBy,
      enteredDate: s.enteredDate,
      modifiedDate: s.modifiedDate,
    }));

    const flatItems = sections.flatMap((s) =>
      s.items.map((it) => ({
        itemId: it.itemId, // < 0 ? undefined : it.itemId,
        sectionId: s.sectionId, // < 0 ? undefined : s.sectionId, // server will map after save if section is new
        itemName: it.itemName,
        isSelected: it.isSelected,
        isActive: it.isActive,
        enteredBy: it.enteredBy,
        modifiedBy: it.modifiedBy,
        enteredDate: it.enteredDate,
        modifiedDate: it.modifiedDate,
        questionType: transformQuestionTypeForDB(it.questionType), // Transform for DB storage
        allowImages: it.allowImages,
        allowComment: it.allowComment,
      }))
    );

    const flatOptions = sections.flatMap((s) =>
      s.sectionType !== "Checklist"
        ? []
        : s.items.flatMap((it) => {
            const optionsToUse = it.questionType === "Multi-Select"
              ? it.multiSelectOptions
              : it.questionType === "Single-Select"
              ? it.singleSelectOptions
              : [];
            return optionsToUse.map((o) => ({
              optionId: o.optionId,
              itemId: it.itemId,
              optionText: o.optionText,
              isActive: o.isActive,
            }));
          })
    );

    const t: ChecklistTemplate = {
      ...template,
      templateId:
        template.templateId && template.templateId > 0
          ? template.templateId
          : undefined,
    };
    return {
      template: t,
      sections: flatSections,
      items: flatItems,
      options: flatOptions,
    };
  }, [template, sections]);

  // Notify parent of form data changes
  useEffect(() => {
    if (onFormDataChange) {
      onFormDataChange(request, hasAnyErrors(errors));
    }
  }, [request, errors, onFormDataChange]);

  return (
    <Box
      sx={{
        p: 2,
        margin: "0 auto",
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* <Typography variant="h4" gutterBottom>
        Create New Template
      </Typography> */}

      <Box sx={{ flex: 1, overflow: "auto" }}>
        <Grid container spacing={3} alignItems="flex-start">
          <Grid
            item
            xs={12}
            md={12}
            sx={{
              width: "100%",
            }}
          >
            {/* Template Name Section */}
            <Card sx={{ mb: 1, mt: 1, boxShadow: "none" }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Template Name
                </Typography>
                <TextField
                  label="Template Name *"
                  value={template.name}
                  onChange={(e) =>
                    setTemplate((prev) => ({ ...prev, name: e.target.value }))
                  }
                  variant="outlined"
                  size="small"
                  inputProps={{ maxLength: 50 }}
                  sx={{
                    width: "100%",
                    "& .MuiInputBase-root": {
                      maxHeight: "80px",
                      overflow: "auto",
                    },
                  }}
                  error={!!errors.templateName}
                  helperText={errors.templateName || ""}
                />
              </CardContent>
            </Card>

            {/* Images Section */}
            <Card variant="outlined" sx={{ mb: 3 }}>
              <CardHeader
                avatar={<ImageIcon color="primary" />}
                title="Images"
                titleTypographyProps={{ variant: "h6" }}
              />
              <CardContent>
                {/* <Typography
                  variant="h6"
                  gutterBottom
                  sx={{
                    backgroundColor: theme.palette.secondary.main,
                    color: theme.palette.secondary.contrastText,
                    px: 2,
                    py: 1,
                    borderRadius: 1,
                    fontWeight: 600,
                    display: "inline-block",
                    mb: 2,
                  }}
                >
                  <ImageIcon className="h-5 w-5 text-primary" />
                  Images
                </Typography> */}
                {(() => {
                  const imageSection = sections.find(
                    (s) => s.sectionType === "Images"
                  );

                  const addImageItem = (itemName: string) => {
                    if (imageSection) {
                      setSections((prev) =>
                        prev.map((s) => {
                          if (s.sectionId !== imageSection.sectionId) return s;
                          const newItem = {
                            ...createItem(
                              imageSection.sectionId,
                              s.sectionType
                            ),
                            itemName,
                            isSelected: true, // Newly added image items are checked by default
                            enteredBy: user?.userName || "system",
                            modifiedBy: user?.userName || "system",
                          };
                          return { ...s, items: [...s.items, newItem] };
                        })
                      );
                    }
                  };

                  return (
                    <Box>
                      {imageSection && (
                        <Box>
                          <Grid
                            container
                            spacing={1}
                            alignItems="center"
                            sx={{ mb: 2, width: "100%" }}
                          >
                            <Grid item xs={12} md={9}>
                              <TextField
                                placeholder="Image Name"
                                size="small"
                                inputProps={{ maxLength: 50 }}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter" && !e.shiftKey) {
                                    e.preventDefault();
                                    const target = e.target as HTMLInputElement;
                                    if (target.value.trim()) {
                                      addImageItem(target.value.trim());
                                      target.value = "";
                                    }
                                  }
                                }}
                                sx={{
                                  width: "100%",
                                }}
                                error={false}
                                helperText=""
                              />
                            </Grid>
                            <Grid item xs={12} md={3}>
                              <Button
                                fullWidth
                                sx={{ textTransform: "capitalize" }}
                                variant="contained"
                                startIcon={<AddIcon />}
                                onClick={() => {
                                  const input = document.querySelector(
                                    'input[placeholder="Image Name"]'
                                  ) as HTMLInputElement;
                                  if (input && input.value.trim()) {
                                    addImageItem(input.value.trim());
                                    input.value = "";
                                  }
                                }}
                              >
                                Add
                              </Button>
                            </Grid>
                          </Grid>

                          {imageSection.items.length > 0 && (
                            <Box
                              sx={{
                                maxHeight: "200px",
                                overflow: "auto",
                                border: "1px solid #e0e0e0",
                                borderRadius: 1,
                                p: 1,
                              }}
                            >
                              <Grid container spacing={1}>
                                {imageSection.items.map((item, index) => (
                                  <Grid item xs={12} sm={6} key={index}>
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        p: 1,
                                        border: "1px solid #e0e0e0",
                                        borderRadius: 1,
                                        bgcolor: "background.paper",
                                        minHeight: "40px",
                                        maxHeight: "80px",
                                        overflow: "hidden",
                                      }}
                                    >
                                      <Checkbox
                                        checked={item.isSelected}
                                        onChange={(e) => {
                                          updateItem(
                                            imageSection.sectionId,
                                            item.itemId,
                                            {
                                              isSelected: e.target.checked,
                                            }
                                          );
                                        }}
                                      />
                                      <Typography
                                        sx={{
                                          flexGrow: 1,
                                          ml: 1,
                                          fontSize: "0.875rem",
                                          overflow: "hidden",
                                          textOverflow: "ellipsis",
                                          display: "-webkit-box",
                                          WebkitLineClamp: 3,
                                          WebkitBoxOrient: "vertical",
                                          cursor: "pointer",
                                          "&:hover": {
                                            textDecoration: "underline",
                                            color: "primary.main",
                                          },
                                        }}
                                        onClick={() => {
                                          setTextDialog({
                                            open: true,
                                            title: "Image Item Details",
                                            text: item.itemName,
                                          });
                                        }}
                                      >
                                        {item.itemName}
                                      </Typography>
                                      {/* Only show delete icon for non-default items */}
                                      {![
                                        "Front",
                                        "Left",
                                        "Back",
                                        "Right",
                                      ].includes(item.itemName) && (
                                        <IconButton
                                          size="small"
                                          onClick={() => {
                                            removeItem(
                                              imageSection.sectionId,
                                              item.itemId
                                            );
                                          }}
                                        >
                                          <DeleteIcon
                                            color="error"
                                            fontSize="small"
                                          />
                                        </IconButton>
                                      )}
                                    </Box>
                                  </Grid>
                                ))}
                              </Grid>
                            </Box>
                          )}
                        </Box>
                      )}
                    </Box>
                  );
                })()}
              </CardContent>
            </Card>

            {/* Videos Section 
            <Card variant="outlined" sx={{ mb: 3 }}>
              <CardHeader
                avatar={<VideoLibrary color="primary" />}
                title="Videos"
                titleTypographyProps={{ variant: "h6" }}
              />
              <CardContent>
                {(() => {
                  const videoSection = sections.find(
                    (s) => s.sectionType === "Videos"
                  );

                  const addVideoItem = (itemName: string) => {
                    if (videoSection) {
                      setSections((prev) =>
                        prev.map((s) => {
                          if (s.sectionId !== videoSection.sectionId) return s;
                          const newItem = {
                            ...createItem(
                              videoSection.sectionId,
                              s.sectionType
                            ),
                            itemName,
                            enteredBy: user?.userName || "system",
                            modifiedBy: user?.userName || "system",
                          };
                          return { ...s, items: [...s.items, newItem] };
                        })
                      );
                    }
                  };

                  return (
                    <Box>
                      {videoSection && (
                        <Box>
                          <Grid
                            container
                            spacing={1}
                            alignItems="center"
                            sx={{ mb: 2, width: "100%" }}
                          >
                            <Grid item xs={9.5}>
                              <TextField
                                placeholder="Video Name"
                                size="small"
                                inputProps={{ maxLength: 50 }}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter" && !e.shiftKey) {
                                    e.preventDefault();
                                    const target = e.target as HTMLInputElement;
                                    if (target.value.trim()) {
                                      addVideoItem(target.value.trim());
                                      target.value = "";
                                    }
                                  }
                                }}
                                sx={{
                                  width: "100%",
                                }}
                                error={false}
                                helperText=""
                              />
                            </Grid>
                            <Grid item xs={2.5}>
                              <Button
                                sx={{ textTransform: "capitalize" }}
                                variant="contained"
                                startIcon={<AddIcon />}
                                onClick={() => {
                                  const input = document.querySelector(
                                    'input[placeholder="Video Name"]'
                                  ) as HTMLInputElement;
                                  if (input && input.value.trim()) {
                                    addVideoItem(input.value.trim());
                                    input.value = "";
                                  }
                                }}
                              >
                                Add
                              </Button>
                            </Grid>
                          </Grid>

                          {videoSection.items.length > 0 && (
                            <Box
                              sx={{
                                maxHeight: "200px",
                                overflow: "auto",
                                border: "1px solid #e0e0e0",
                                borderRadius: 1,
                                p: 1,
                              }}
                            >
                              <Grid container spacing={1}>
                                {videoSection.items.map((item, index) => (
                                  <Grid item xs={6} key={index}>
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        p: 1,
                                        border: "1px solid #e0e0e0",
                                        borderRadius: 1,
                                        bgcolor: "background.paper",
                                        minHeight: "40px",
                                        maxHeight: "80px",
                                        overflow: "hidden",
                                      }}
                                    >
                                      <Checkbox
                                        checked={item.isSelected}
                                        onChange={(e) => {
                                          updateItem(
                                            videoSection.sectionId,
                                            item.itemId,
                                            {
                                              isSelected: e.target.checked,
                                            }
                                          );
                                        }}
                                      />
                                      <Typography
                                        sx={{
                                          flexGrow: 1,
                                          ml: 1,
                                          fontSize: "0.875rem",
                                          overflow: "hidden",
                                          textOverflow: "ellipsis",
                                          display: "-webkit-box",
                                          WebkitLineClamp: 3,
                                          WebkitBoxOrient: "vertical",
                                          cursor: "pointer",
                                          "&:hover": {
                                            textDecoration: "underline",
                                            color: "primary.main",
                                          },
                                        }}
                                        onClick={() => {
                                          setTextDialog({
                                            open: true,
                                            title: "Video Item Details",
                                            text: item.itemName,
                                          });
                                        }}
                                      >
                                        {item.itemName}
                                      </Typography>
                                      <IconButton
                                        size="small"
                                        onClick={() => {
                                          removeItem(
                                            videoSection.sectionId,
                                            item.itemId
                                          );
                                        }}
                                      >
                                        <DeleteIcon
                                          color="error"
                                          fontSize="small"
                                        />
                                      </IconButton>
                                    </Box>
                                  </Grid>
                                ))}
                              </Grid>
                            </Box>
                          )}
                        </Box>
                      )}
                    </Box>
                  );
                })()}
              </CardContent>
            </Card>

            */}

            {/* Checklist Section */}
            <Card variant="outlined" sx={{ mb: 3 }}>
              <CardHeader
                avatar={<Checklist color="primary" />}
                title="Checklist"
                titleTypographyProps={{ variant: "h6" }}
              />
              <CardContent>
                {/* <Typography
                  variant="h6"
                  gutterBottom
                  sx={{
                    backgroundColor: theme.palette.success.main,
                    color: theme.palette.success.contrastText,
                    px: 2,
                    py: 1,
                    borderRadius: 1,
                    fontWeight: 600,
                    display: "inline-block",
                    mb: 2,
                  }}
                >
                  Checklist
                </Typography> */}
                <Box>
                  {sections
                    .filter((s) => s.sectionType === "Checklist")
                    .map((section, sectionIndex) => (
                      <Paper key={sectionIndex} sx={{ p: 2, mb: 2 }}>
                        <Typography variant="subtitle1" gutterBottom>
                          Section {sectionIndex + 1}
                        </Typography>
                        <TextField
                          label="Section Name *"
                          value={section.sectionName || ""}
                          onChange={(e) => {
                            updateSection(section.sectionId, {
                              sectionName: e.target.value,
                            });
                          }}
                          size="small"
                          inputProps={{ maxLength: 50 }}
                          sx={{ mb: 2, width: "100%" }}
                          error={
                            !!errors.sectionErrors[section.sectionId]
                              ?.sectionName
                          }
                          helperText={
                            errors.sectionErrors[section.sectionId]
                              ?.sectionName || ""
                          }
                        />
                        {/* Questions in this section */}
                        {section.items.map((item, itemIndex) => (
                          <Box
                            key={itemIndex}
                            sx={{
                              mb: 2,
                              p: 3,
                              border: "1px solid #e0e0e0",
                              borderRadius: 1,
                            }}
                          >
                            <Typography variant="subtitle2" gutterBottom>
                              Question {itemIndex + 1}
                            </Typography>
                            <TextField
                              label="Question Name *"
                              value={item.itemName}
                              onChange={(e) => {
                                updateItem(section.sectionId, item.itemId, {
                                  itemName: e.target.value,
                                });
                              }}
                              size="small"
                              multiline
                              rows={2}
                              inputProps={{ maxLength: 500 }}
                              sx={{ mb: 2, width: "100%" }}
                              error={!!errors.itemErrors[item.itemId]?.itemName}
                              helperText={
                                errors.itemErrors[item.itemId]?.itemName || ""
                              }
                            />
                            <Box sx={{ mb: 2, display: "flex", gap: 3, flexWrap: "wrap" }}>
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    checked={item.allowImages}
                                    onChange={(e) => {
                                      updateItem(
                                        section.sectionId,
                                        item.itemId,
                                        {
                                          allowImages: e.target.checked,
                                        }
                                      );
                                    }}
                                  />
                                }
                                label="Allow Images"
                              />
                              {(item.questionType === "Single-Select" ||
                                item.questionType === "Multi-Select") && (
                                <FormControlLabel
                                  control={
                                    <Checkbox
                                      checked={item.allowComment}
                                      onChange={(e) => {
                                        updateItem(
                                          section.sectionId,
                                          item.itemId,
                                          {
                                            allowComment: e.target.checked,
                                          }
                                        );
                                      }}
                                    />
                                  }
                                  label="Comment"
                                />
                              )}
                            </Box>
                            <Box sx={{ mb: 2 }}>
                              <Typography variant="body2" gutterBottom>
                                Question Type:
                              </Typography>
                              <ToggleButtonGroup
                                value={item.questionType}
                                exclusive
                                onChange={(e, newValue) => {
                                  if (newValue) {
                                    setQuestionType(
                                      section.sectionId,
                                      item.itemId,
                                      newValue
                                    );
                                  }
                                }}
                                size="small"
                                sx={{
                                  backgroundColor:
                                    theme.palette.background.paper,
                                  border: `1px solid ${theme.palette.divider}`,
                                  flexWrap: "wrap",
                                  width: "100%",
                                  "& .MuiToggleButton-root": {
                                    flex: { xs: "1 1 100%", sm: "1 1 auto" },
                                    minWidth: { xs: "100%", sm: "auto" },
                                    color: theme.palette.text.primary,
                                    backgroundColor:
                                      theme.palette.background.paper,
                                    border: `1px solid ${theme.palette.divider}`,
                                    "&:hover": {
                                      backgroundColor:
                                        theme.palette.action.hover,
                                    },
                                    "&.Mui-selected": {
                                      backgroundColor:
                                        theme.palette.primary.main,
                                      color: theme.palette.primary.contrastText,
                                      "&:hover": {
                                        backgroundColor:
                                          theme.palette.primary.dark,
                                      },
                                    },
                                  },
                                }}
                              >
                                <ToggleButton
                                  sx={{
                                    textTransform: "capitalize",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 0.5,
                                  }}
                                  value="Text"
                                >
                                  <TextIcon />
                                  Text
                                </ToggleButton>
                                <ToggleButton
                                  sx={{
                                    textTransform: "capitalize",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 0.5,
                                  }}
                                  value="Multi-Select"
                                >
                                  <MultiSelectIcon />
                                  Multi-Select
                                </ToggleButton>
                                <ToggleButton
                                  sx={{
                                    textTransform: "capitalize",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 0.5,
                                  }}
                                  value="Single-Select"
                                >
                                  <SingleSelectIcon />
                                  Single-Select
                                </ToggleButton>
                              </ToggleButtonGroup>
                            </Box>
                            {/* Options for Multi-Select and Single-Select */}
                            {(item.questionType === "Multi-Select" ||
                              item.questionType === "Single-Select") && (
                              <Box sx={{ mb: 2 }}>
                                <Typography variant="body2" gutterBottom>
                                  Options:
                                </Typography>
                                {((item.questionType === "Multi-Select" ? item.multiSelectOptions : item.singleSelectOptions) || []).length > 0 && (
                                  <Box
                                    sx={{
                                      maxHeight: "150px",
                                      overflow: "auto",
                                      border: "1px solid #e0e0e0",
                                      borderRadius: 1,
                                      p: 1,
                                      mb: 1,
                                    }}
                                  >
                                    <Grid container spacing={1}>
                                      {(item.questionType === "Multi-Select" ? item.multiSelectOptions : item.singleSelectOptions).map(
                                        (option, optionIndex) => (
                                          <Grid item xs={6} key={optionIndex}>
                                            <Chip
                                              label={option.optionText}
                                              onDelete={() => {
                                                removeOption(
                                                  section.sectionId,
                                                  item.itemId,
                                                  option.optionId
                                                );
                                              }}
                                              onClick={() => {
                                                setTextDialog({
                                                  open: true,
                                                  title: "Option Details",
                                                  text: option.optionText,
                                                });
                                              }}
                                              sx={{
                                                width: "100%",
                                                justifyContent: "space-between",
                                                maxHeight: "60px",
                                                cursor: "pointer",
                                                "&:hover": {
                                                  backgroundColor:
                                                    "primary.light",
                                                  color: "primary.contrastText",
                                                },
                                                "& .MuiChip-label": {
                                                  overflow: "hidden",
                                                  textOverflow: "ellipsis",
                                                  display: "-webkit-box",
                                                  WebkitLineClamp: 2,
                                                  WebkitBoxOrient: "vertical",
                                                },
                                                "& .MuiChip-deleteIcon": {
                                                  marginLeft: "auto",
                                                  color:
                                                    theme.palette.error.main,
                                                  "&:hover": {
                                                    color:
                                                      theme.palette.error.dark,
                                                    backgroundColor:
                                                      theme.palette.error.light,
                                                    borderRadius: "50%",
                                                  },
                                                },
                                              }}
                                            />
                                          </Grid>
                                        )
                                      )}
                                    </Grid>
                                  </Box>
                                )}
                                <Grid container spacing={2} alignItems="center">
                                  <Grid item xs={12} md={10}>
                                    <TextField
                                      placeholder="Option Name"
                                      size="small"
                                      fullWidth
                                      multiline
                                      rows={2}
                                      inputProps={{ maxLength: 500 }}
                                      onKeyDown={(e) => {
                                        if (e.key === "Enter" && !e.shiftKey) {
                                          e.preventDefault();
                                          const target =
                                            e.target as HTMLInputElement;
                                          if (target.value.trim()) {
                                            addOption(
                                              section.sectionId,
                                              item.itemId,
                                              target.value.trim()
                                            );
                                            target.value = "";
                                          }
                                        }
                                      }}
                                      error={
                                        !!errors.itemErrors[item.itemId]
                                          ?.options
                                      }
                                      helperText={
                                        errors.itemErrors[item.itemId]
                                          ?.options || ""
                                      }
                                    />
                                  </Grid>
                                  <Grid item xs={12} md={2}>
                                    <Button
                                      fullWidth
                                      sx={{
                                        textTransform: "capitalize",
                                        mr: { md: 1, xs: 0 },
                                      }}
                                      variant="contained"
                                      size="small"
                                      startIcon={<AddIcon />}
                                      onClick={(e) => {
                                        const button = e.currentTarget;
                                        const input =
                                          button.parentElement?.previousElementSibling?.querySelector(
                                            "textarea"
                                          ) as HTMLTextAreaElement;
                                        if (input && input.value.trim()) {
                                          addOption(
                                            section.sectionId,
                                            item.itemId,
                                            input.value.trim()
                                          );
                                          input.value = "";
                                        }
                                      }}
                                    >
                                      Add
                                    </Button>
                                  </Grid>
                                </Grid>
                              </Box>
                            )}
                            <Button
                              sx={{ textTransform: "capitalize" }}
                              variant="outlined"
                              color="error"
                              startIcon={<DeleteIcon color="error" />}
                              size="small"
                              onClick={() => {
                                removeItem(section.sectionId, item.itemId);
                              }}
                            >
                              Delete Question
                            </Button>
                          </Box>
                        ))}

                        {/* Add Question Button */}
                        <Box
                          sx={{
                            display: "flex",
                            gap: 2,
                            mb: 2,
                            flexDirection: { xs: "column", sm: "row" },
                          }}
                        >
                          <Button
                            sx={{
                              textTransform: "capitalize",
                              width: { xs: "100%", sm: "auto" },
                            }}
                            size="small"
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={() => {
                              addItem(section.sectionId);
                            }}
                          >
                            New Question
                          </Button>
                          <Button
                            sx={{
                              textTransform: "capitalize",
                              width: { xs: "100%", sm: "auto" },
                            }}
                            size="small"
                            variant="outlined"
                            startIcon={<DeleteIcon color="error" />}
                            color="error"
                            onClick={() => removeSection(section.sectionId)}
                          >
                            Delete Section
                          </Button>
                        </Box>
                      </Paper>
                    ))}

                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={addChecklistSection}
                    sx={{ width: "100%", textTransform: "capitalize" }}
                  >
                    New Section
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>

      {/* Full Text Dialog */}
      <Dialog
        open={textDialog.open}
        onClose={() => setTextDialog({ ...textDialog, open: false })}
        maxWidth="xs"
        fullWidth
        sx={{
          "& .MuiDialog-paper": {
            maxHeight: "90vh",
            borderRadius: 2,
          },
        }}
      >
        <DialogTitle
          sx={(theme) => ({
            m: 0,
            backgroundColor:
              theme.palette.mode === "light"
                ? theme.palette.primary.main
                : "#181F2A",
            color:
              theme.palette.mode === "light"
                ? theme.palette.primary.contrastText
                : "#fff",
            p: 1,
            borderBottom: `1px solid ${theme.palette.divider}`,
          })}
        >
          {textDialog.title}
        </DialogTitle>
        <DialogContent>
          <Typography
            sx={{
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              lineHeight: 1.6,
              fontSize: "1rem",
              mt: 1,
            }}
          >
            {textDialog.text}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            sx={{ textTransform: "capitalize" }}
            onClick={() => setTextDialog({ ...textDialog, open: false })}
            variant="contained"
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ChecklistTemplateComponent;
